import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import AnimatedWordmark from "@/components/marocanada/AnimatedWordmark";
import ParticleDrift from "@/components/marocanada/ParticleDrift";
import { trackCtaClick, getEligibilityUrl } from "@/lib/analytics";

const Hero = () => {
  const { t, lang } = useLang();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden"
      style={{ background: "#0D1117" }}
    >
      {/* Split-world background */}
      <div className="absolute inset-0 grid grid-cols-2">
        {/* LEFT — Marrakech (Koutoubia + Atlas) */}
        <div className="relative overflow-hidden">
          <div
            className="absolute inset-0 slow-drift"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1597212618440-806262de4f6b?crop=entropy&cs=srgb&fm=webp&q=72&w=1200')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          {/* gentle edge fade toward seam */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to right, rgba(13,17,23,0.25) 0%, transparent 35%, rgba(13,17,23,0.45) 100%)",
            }}
          />
        </div>

        {/* RIGHT — Canadian skyline, fog-lit */}
        <div className="relative overflow-hidden">
          <div
            className="absolute inset-0 slow-drift"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1519178614-68673b201f36?crop=entropy&cs=srgb&fm=webp&q=72&w=1200')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              animationDelay: "-12s",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to left, rgba(13,17,23,0.25) 0%, transparent 35%, rgba(13,17,23,0.45) 100%)",
            }}
          />
        </div>
      </div>

      {/* VERTICAL LIGHT COLUMN — the doorway seam, saffron→teal */}
      <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[180px] pointer-events-none">
        <div
          className="absolute inset-0 seam-pulse"
          style={{
            background:
              "radial-gradient(ellipse 100% 70% at center, rgba(232,160,69,0.20) 0%, transparent 70%)",
          }}
        />
        <div
          className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[3px]"
          style={{
            background:
              "linear-gradient(to bottom, transparent 0%, rgba(232,160,69,0.55) 22%, rgba(212,170,100,0.85) 50%, rgba(47,191,173,0.55) 78%, transparent 100%)",
            filter: "blur(1px)",
          }}
        />
        <div
          className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[1px]"
          style={{
            background:
              "linear-gradient(to bottom, transparent 0%, rgba(255,235,200,0.9) 50%, transparent 100%)",
          }}
        />
      </div>

      {/* Bottom fade */}
      <div
        className="absolute inset-x-0 bottom-0 h-40 pointer-events-none"
        style={{ background: "linear-gradient(to bottom, transparent, #0D1117 92%)" }}
      />

      {/* Particle drift (warm bottom-left → cool top-right) */}
      <ParticleDrift count={42} />

      {/* Grain texture top layer */}
      <div className="grain-overlay" />

      {/* CONTENT */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-3 mb-12 font-mono-accent text-[10px] sm:text-xs text-[#E8A045]"
        >
          <span className="block h-px w-10 bg-[#E8A045]/70" />
          <span data-testid="hero-eyebrow">{t.hero.eyebrow}</span>
          <span className="block h-px w-10 bg-[#E8A045]/70" />
        </motion.div>

        {/* GIANT WORDMARK with collision animation */}
        <div data-testid="hero-headline" className="flex justify-center">
          <AnimatedWordmark
            className="text-[3.5rem] sm:text-[5.5rem] md:text-[7.5rem] lg:text-[9.5rem] xl:text-[10.5rem] leading-[0.95] font-normal"
            cedar="#C8A55E"
            snow="#F5F2EE"
          />
        </div>

        <motion.p
          data-testid="hero-tagline"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 font-display italic text-xl sm:text-2xl lg:text-[1.75rem] text-[#F5F2EE]/85 font-light"
        >
          {t.hero.tagline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.55, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14"
        >
          <a
            href={getEligibilityUrl(lang)}
            data-testid="hero-cta-button"
            onClick={() => trackCtaClick("hero")}
            className="inner-border-btn group relative inline-flex items-center gap-3 bg-[#2FBFAD] hover:bg-[#239B8C] text-[#0D1117] px-10 py-5 rounded-full text-base sm:text-lg tracking-wide font-medium transition-all"
          >
            <span>{t.hero.cta}</span>
            <ArrowRight
              className="w-5 h-5 transition-transform group-hover:translate-x-1"
              strokeWidth={2}
            />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
